import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  Alert,
  Paper,
  Card,
  CardContent,
  useTheme,
} from '@mui/material';
import {
  Delete,
  Add,
  Check,
  Bolt,
  Info,
} from '@mui/icons-material';
import { Transaction, LoyaltyProgram } from '../../types';
import { format } from 'date-fns';

interface QuickEntryModeProps {
  open: boolean;
  onClose: () => void;
  onSave: (transactions: Omit<Transaction, 'id'>[]) => Promise<void>;
  programs: LoyaltyProgram[];
  defaultProgramId?: string;
}

const QuickEntryMode: React.FC<QuickEntryModeProps> = ({
  open,
  onClose,
  onSave,
  programs,
  defaultProgramId,
}) => {
  const [transactions, setTransactions] = useState<Omit<Transaction, 'id'>[]>([]);
  const [currentTransaction, setCurrentTransaction] = useState({
    programId: defaultProgramId || '',
    date: format(new Date(), 'yyyy-MM-dd'),
    description: '',
    points: '',
    type: 'earned' as 'earned' | 'redeemed',
  });
  const [saving, setSaving] = useState(false);

  const descriptionRef = useRef<HTMLInputElement>(null);
  const pointsRef = useRef<HTMLInputElement>(null);
  const theme = useTheme();

  useEffect(() => {
    if (open) {
      // Focus on description field when dialog opens
      setTimeout(() => descriptionRef.current?.focus(), 100);
    }
  }, [open]);

  const handleAddTransaction = () => {
    if (!currentTransaction.programId || !currentTransaction.description || !currentTransaction.points) {
      return;
    }

    const newTransaction: Omit<Transaction, 'id'> = {
      programId: currentTransaction.programId,
      date: new Date(currentTransaction.date),
      description: currentTransaction.description,
      type: currentTransaction.type,
      points: parseFloat(currentTransaction.points),
      balance: 0,
    };

    setTransactions([...transactions, newTransaction]);

    // Reset for next entry, keeping program and date
    setCurrentTransaction({
      ...currentTransaction,
      description: '',
      points: '',
    });

    // Focus back on description for quick entry
    setTimeout(() => descriptionRef.current?.focus(), 50);
  };

  const handleKeyPress = (e: React.KeyboardEvent, field: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      if (field === 'description') {
        // Move to points field
        pointsRef.current?.focus();
      } else if (field === 'points') {
        // Add transaction and go back to description
        handleAddTransaction();
      }
    }
  };

  const handleDeleteTransaction = (index: number) => {
    setTransactions(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (transactions.length === 0) return;

    setSaving(true);
    try {
      await onSave(transactions);
      setTransactions([]);
      setCurrentTransaction({
        programId: defaultProgramId || '',
        date: format(new Date(), 'yyyy-MM-dd'),
        description: '',
        points: '',
        type: 'earned',
      });
      onClose();
    } catch (error) {
      console.error('Failed to save transactions:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (transactions.length > 0) {
      if (!window.confirm(`You have ${transactions.length} unsaved transaction(s). Discard them?`)) {
        return;
      }
    }
    setTransactions([]);
    onClose();
  };

  const totalPoints = transactions.reduce((sum, t) => 
    t.type === 'earned' ? sum + t.points : sum - t.points, 0
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Bolt sx={{ color: 'white', fontSize: 24 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={700}>
              Quick Entry Mode
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Rapid transaction entry with keyboard shortcuts
            </Typography>
          </Box>
          <Chip 
            label={`${transactions.length} transaction${transactions.length !== 1 ? 's' : ''}`}
            size="small" 
            color={transactions.length > 0 ? 'primary' : 'default'}
            sx={{ fontWeight: 600 }}
          />
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Alert 
          severity="info" 
          icon={<Info />}
          sx={{ 
            mb: 3,
            borderRadius: 2,
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)',
            border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.2)'}`,
          }}
        >
          <Typography variant="body2" fontWeight={600} gutterBottom>
            ⌨️ Keyboard shortcuts:
          </Typography>
          <Typography variant="caption" component="div">
            • Press <strong>Enter</strong> to move between fields and add transactions
            <br />
            • Press <strong>Tab</strong> to navigate fields manually
            <br />
            • Keep program/date same for batch entry
          </Typography>
        </Alert>

        {/* Quick Entry Form */}
        <Card 
          elevation={0}
          sx={{ 
            mb: 3,
            border: `2px solid ${theme.palette.primary.main}`,
            borderRadius: 3,
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, rgba(66, 153, 225, 0.1) 0%, rgba(99, 179, 237, 0.05) 100%)'
              : 'linear-gradient(135deg, rgba(26, 54, 93, 0.05) 0%, rgba(44, 82, 130, 0.02) 100%)',
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography variant="subtitle2" fontWeight={700} color="primary" gutterBottom sx={{ mb: 2 }}>
              Current Transaction
            </Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Program</InputLabel>
                <Select
                  value={currentTransaction.programId}
                  onChange={(e) => setCurrentTransaction({ ...currentTransaction, programId: e.target.value })}
                  label="Program"
                  sx={{
                    bgcolor: theme.palette.background.paper,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                    },
                  }}
                >
                  {programs.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography>{p.name}</Typography>
                        <Chip label={p.type.replace('_', ' ')} size="small" variant="outlined" />
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Date"
                type="date"
                value={currentTransaction.date}
                onChange={(e) => setCurrentTransaction({ ...currentTransaction, date: e.target.value })}
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{
                  bgcolor: theme.palette.background.paper,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                  },
                }}
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 120px', gap: 2, mb: 2 }}>
              <TextField
                inputRef={descriptionRef}
                label="Description"
                value={currentTransaction.description}
                onChange={(e) => setCurrentTransaction({ ...currentTransaction, description: e.target.value })}
                onKeyPress={(e) => handleKeyPress(e, 'description')}
                size="small"
                placeholder="e.g., Restaurant, Flight"
                autoFocus
                sx={{
                  bgcolor: theme.palette.background.paper,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                  },
                }}
              />

              <TextField
                inputRef={pointsRef}
                label="Points"
                type="number"
                value={currentTransaction.points}
                onChange={(e) => setCurrentTransaction({ ...currentTransaction, points: e.target.value })}
                onKeyPress={(e) => handleKeyPress(e, 'points')}
                size="small"
                placeholder="Amount"
                sx={{
                  bgcolor: theme.palette.background.paper,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                  },
                }}
              />

              <FormControl size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  value={currentTransaction.type}
                  onChange={(e) => setCurrentTransaction({ ...currentTransaction, type: e.target.value as 'earned' | 'redeemed' })}
                  label="Type"
                  sx={{
                    bgcolor: theme.palette.background.paper,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                    },
                  }}
                >
                  <MenuItem value="earned">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#10b981' }} />
                      Earned
                    </Box>
                  </MenuItem>
                  <MenuItem value="redeemed">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ef4444' }} />
                      Redeemed
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddTransaction}
                disabled={!currentTransaction.programId || !currentTransaction.description || !currentTransaction.points}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                }}
              >
                Add Transaction (Enter ↵)
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Transaction List */}
        {transactions.length > 0 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                Transactions to Save
              </Typography>
              <Chip
                label={`Net: ${totalPoints >= 0 ? '+' : ''}${totalPoints.toLocaleString()} points`}
                color={totalPoints >= 0 ? 'success' : 'error'}
                size="small"
                sx={{ fontWeight: 700 }}
              />
            </Box>

            <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
              {transactions.map((txn, index) => {
                const program = programs.find(p => p.id === txn.programId);
                return (
                  <Card
                    key={index}
                    elevation={0}
                    sx={{ 
                      mb: 1.5,
                      border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                      borderLeft: `4px solid ${txn.type === 'earned' ? '#10b981' : '#ef4444'}`,
                      borderRadius: 2,
                      transition: 'all 0.2s',
                      '&:hover': {
                        boxShadow: theme.palette.mode === 'dark' 
                          ? '0 4px 12px rgba(0, 0, 0, 0.3)'
                          : '0 4px 12px rgba(0, 0, 0, 0.1)',
                      },
                    }}
                  >
                    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="body2" fontWeight={600}>
                              {txn.description}
                            </Typography>
                            <Chip 
                              label={program?.name} 
                              size="small" 
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          </Box>
                          <Box sx={{ display: 'flex', gap: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                              {format(txn.date, 'MMM dd, yyyy')}
                            </Typography>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: txn.type === 'earned' ? '#10b981' : '#ef4444',
                                fontWeight: 700,
                              }}
                            >
                              {txn.type === 'earned' ? '+' : '-'}{txn.points.toLocaleString()} pts
                            </Typography>
                          </Box>
                        </Box>
                        <IconButton 
                          edge="end" 
                          onClick={() => handleDeleteTransaction(index)}
                          size="small"
                          sx={{ 
                            color: 'error.main',
                            '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.1)' }
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          </Box>
        )}

        {transactions.length === 0 && (
          <Box sx={{ 
            textAlign: 'center', 
            py: 6, 
            px: 3,
            borderRadius: 3,
            border: `2px dashed ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
          }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              No transactions added yet
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Fill in the form above and press Enter to start adding transactions
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={<Check />}
          disabled={transactions.length === 0 || saving}
        >
          Save {transactions.length} Transaction{transactions.length !== 1 ? 's' : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickEntryMode;
